import { z } from 'zod';

import { FULLY_MANAGED_ORDER_LIMIT } from '@Common/constants/b2b/limits';
import { ZodOdOrderSide, ZodOdOrderStatus } from '@Common/types/b2b/orders';
import { ZodB2bSavingPlanType } from '@Common/types/b2b/plans';
import { ZodTaxResidenceCode } from '@Common/types/bo/b2b/employees';
import {
  ZodPostB2bIndividualOperationArbitrageBody,
  ZodPostB2bIndividualOperationArbitrageSummaryResponse,
} from '@Common/types/bo/b2b/individual_operations/arbitrage';
import { ZodB2bCorrectionStatus } from '@Common/types/bo/b2b/ops_actions/corrections';
import { BoListQueryBase } from '@Common/types/bo/bo_list_query';
import { ZodBoExportResponse, ZodBoListCsvExportBody } from '@Common/types/bo/exports';
import { buildZodBoListQueryParsed } from '@Common/types/bo/utils/build_zod_bo_list_query';
import { zodIntegerAmount2 } from '@Common/types/currency/currency';
import {
  ZodMutualFundOrderAllocations,
  ZodVoluntaryDeductibility,
} from '@Common/types/explore/mutual_fund_order';
import { ZodB2BPaymentMethod } from '@Common/types/funding/payment_methods';
import {
  ZodTransactionOrderCancellationInitiator,
  ZodTransactionOrderCancellationReason,
} from '@Common/types/profile/transactions/enums';
import { ZodIDPathParam, ZodId } from '@Common/types/request';
import { createZodArrayable } from '@Common/types/zod/zod-arrayable';
import { ZodDateString, ZodDateTimeString } from '@Common/types/zod/zod-date-string';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { ZodJSONObject } from '@Common/types/zod/zod-json';
import { ZodNumberQuery } from '@Common/types/zod/zod-number-query';
import { createZodParamsValidator } from '@Common/types/zod/zod-params';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

export const B2bIndividualOperationTypeValues = [
  'employee_voluntary_deposit',
  'company_voluntary_deposit',
  'arbitrage',
  'collective_arbitrage',
  'rebalancing',
  'withdrawal',
  'transfer_in',
  'transfer_out',
  'participation',
  'profit_sharing',
  'value_sharing_bonus',
  'unused_vacation_days',
  'voluntary_deposit_campaign',
  'matching_contribution',
  'corporate_action',
  'ccb_matured_election',
  'withdrawal_at_maturity',
  'capital_increase',
] as const;

export type B2bIndividualOperationType = (typeof B2bIndividualOperationTypeValues)[number];

export const ZodB2bIndividualOperationType = z.enum(B2bIndividualOperationTypeValues);

export const BO_EXTERNAL_VISIBLE_INDIVIDUAL_OPERATION_TYPES: readonly B2bIndividualOperationType[] =
  ['employee_voluntary_deposit'];

export const BO_INTERNAL_VISIBLE_INDIVIDUAL_OPERATION_TYPES: readonly B2bIndividualOperationType[] =
  ['employee_voluntary_deposit', 'arbitrage'];

export const B2bIndividualOperationInitiatorValues = ['backoffice', 'user'] as const;
export type B2bIndividualOperationInitiator =
  (typeof B2bIndividualOperationInitiatorValues)[number];
export const ZodB2bIndividualOperationInitiator = z.enum(B2bIndividualOperationInitiatorValues);

const ZodGetB2bIndividualOperationsOneResponse = z.object({
  id: ZodId,
  b2b_company_plan_id: ZodId.nullable(),
  equipment: ZodTrimmedString.nullable(),
  type: ZodB2bIndividualOperationType,
  execution_date: ZodDateTimeString.nullable(),
  reference_period: ZodTrimmedString,
  status: ZodOdOrderStatus,
  cancellation_reason: ZodTransactionOrderCancellationReason.nullable(),
  cancellation_initiator: ZodTransactionOrderCancellationInitiator.nullable(),
  created_at: ZodDateTimeString,
  updated_at: ZodDateTimeString,
  total_gross_amount: z.number(),
  total_entry_fee_employee_amount: z.number(),
  total_net_amount: z.number(),
  firstname: ZodTrimmedString.nullable(),
  lastname: ZodTrimmedString.nullable(),
  identifier: ZodTrimmedString.nullable(),
  registration_number: ZodTrimmedString.nullable(),
  availability_date: ZodDateTimeString.nullable(),
  plan_type: ZodB2bSavingPlanType.nullable(),
  b2b_employee_id: ZodId.nullable(),
  user_id: ZodId.nullable(),
  b2b_company_id: ZodId.nullable(),
  b2b_contract_id: ZodId.nullable(),
  initiator: ZodB2bIndividualOperationInitiator,
  bo_user_email: ZodTrimmedString.nullable(),
  b2b_recurring_investment_id: ZodId.nullable(),
  has_matching_contribution: z.boolean().optional(),
  b2b_correction_id: ZodId.nullable().optional(),
  b2b_correction_status: ZodB2bCorrectionStatus.nullable().optional(),
});
export type GetB2bIndividualOperationsOneResponse = z.infer<
  typeof ZodGetB2bIndividualOperationsOneResponse
>;

const ZodGetB2bIndividualOperationsResponse = z.array(ZodGetB2bIndividualOperationsOneResponse);
export type GetB2bIndividualOperationsResponse = z.infer<
  typeof ZodGetB2bIndividualOperationsResponse
>;

export const getB2bIndividualOperationsSortColumns = [
  'id',
  'type',
  'status',
  'execution_date',
  'created_at',
  'updated_at',
] as const satisfies (keyof GetB2bIndividualOperationsResponse[number])[];

const ZodFilters = z
  .object({
    id: createZodArrayable(ZodId),
    equipment: createZodArrayable(ZodTrimmedString),
    type: createZodArrayable(ZodB2bIndividualOperationType),
    status: createZodArrayable(ZodOdOrderStatus),
    b2b_employee_id: createZodArrayable(ZodId),
    b2b_company_id: createZodArrayable(ZodId),
    b2b_contract_id: createZodArrayable(ZodId),
    b2b_company_plan_id: createZodArrayable(ZodId),
    user_id: createZodArrayable(ZodId),
    initiator: createZodArrayable(ZodB2bIndividualOperationInitiator),
    b2b_recurring_investment_id: createZodArrayable(ZodId),
    is_recurring_investment: z.boolean(),
    // Calendar-day bounds (YYYY-MM-DD), both inclusive: see listB2bIndividualOperations.
    from_created_at: ZodDateString,
    to_created_at: ZodDateString,
    from_total_gross_amount: z.number(),
    to_total_gross_amount: z.number(),
    from_total_net_amount: z.number(),
    to_total_net_amount: z.number(),
  })
  .partial();

const ZodGetB2bIndividualOperationsQuery = buildZodBoListQueryParsed(
  ZodFilters,
  getB2bIndividualOperationsSortColumns,
  {
    range: [0, 100],
  },
);

export type GetB2bIndividualOperationsQuery = BoListQueryBase<
  z.TypeOf<typeof ZodFilters>,
  (typeof getB2bIndividualOperationsSortColumns)[number]
>;

export const getB2bIndividualOperationsEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetB2bIndividualOperationsQuery,
  responseBody: ZodGetB2bIndividualOperationsResponse,
});

export type GetB2bIndividualOperationsEndpoint = GetEndpointType<
  typeof getB2bIndividualOperationsEndpoint
>;

/**
 * The operations of the list, as its filters and sort select them, in the branded Excel template.
 * `query` is the list query as the list endpoint takes it in its query string.
 */
export const ZodIndividualOperationsExportBody = z.object({
  kind: z.literal('individual_operations'),
  query: ZodBoListCsvExportBody.shape.query,
});
export type IndividualOperationsExportBody = z.infer<typeof ZodIndividualOperationsExportBody>;

export const postB2bIndividualOperationsExportEndpoint = endpoint({
  method: 'POST',
  body: ZodIndividualOperationsExportBody,
  responseBody: ZodBoExportResponse,
});
export type PostB2bIndividualOperationsExportEndpoint = GetEndpointType<
  typeof postB2bIndividualOperationsExportEndpoint
>;

export const ZodGetIndividualOperationsIdParams = createZodParamsValidator(
  z.object({
    id: ZodTrimmedString,
  }),
);

export type GetB2bIndividualOperationOneResponse = z.infer<
  typeof ZodGetB2bIndividualOperationsOneResponse
>;

export const getB2bIndividualOperationEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodGetIndividualOperationsIdParams,
  responseBody: ZodGetB2bIndividualOperationsOneResponse,
});

export type GetB2bIndividualOperationEndpoint = GetEndpointType<
  typeof getB2bIndividualOperationEndpoint
>;

export const postB2bIndividualOperationCancelVoluntaryDepositEndpoint = endpoint({
  method: 'POST',
  pathParams: ZodIDPathParam,
  body: z.object({
    reason: ZodTrimmedString.min(1),
    comment: ZodTrimmedString.nullable(),
  }),
  responseBody: z.object({ b2b_correction_id: ZodId }),
});

export type PostB2bIndividualOperationCancelVoluntaryDepositEndpoint = GetEndpointType<
  typeof postB2bIndividualOperationCancelVoluntaryDepositEndpoint
>;

const ZodGetB2bIndividualOperationOrdersFilters = z
  .object({
    id: createZodArrayable(ZodNumberQuery),
    status: createZodArrayable(ZodOdOrderStatus),
    instrument_id: createZodArrayable(ZodId),
    individual_operation_id: ZodId,
  })
  .partial();

export const getB2bIndividualOperationOrdersSortColumns: (keyof GetB2bIndividualOperationOrderResponse)[] =
  [
    'id',
    'status',
    'isin',
    'instrument_id',
    'entry_fee_employee_amount',
    'gross_amount',
    'net_amount',
  ];

const ZodGetB2bIndividualOperationOrdersQuery = buildZodBoListQueryParsed(
  ZodGetB2bIndividualOperationOrdersFilters,
  getB2bIndividualOperationOrdersSortColumns,
  {
    range: [0, 100],
  },
);

export type GetB2bIndividualOperationOrdersQuery = BoListQueryBase<
  z.TypeOf<typeof ZodGetB2bIndividualOperationOrdersFilters>,
  (typeof getB2bIndividualOperationOrdersSortColumns)[number]
>;

const ZodGetB2bIndividualOperationOrderResponse = z.object({
  id: ZodId,
  status: ZodOdOrderStatus,
  side: ZodOdOrderSide,
  cancellation_reason: ZodTransactionOrderCancellationReason.nullable(),
  cancellation_initiator: ZodTransactionOrderCancellationInitiator.nullable(),
  isin: ZodTrimmedString.nullable(),
  instrument_id: ZodId,
  gross_amount: z.number(),
  net_amount: z.number(),
  entry_fee_employee_amount: z.number(),
  identifier: ZodTrimmedString.nullable(),
  firstname: ZodTrimmedString.nullable(),
  lastname: ZodTrimmedString.nullable(),
  registration_number: ZodTrimmedString.nullable(),
  individual_operation_id: ZodId,
  availability_date: ZodDateTimeString.nullable(),
  entry_fee_company_amount: z.number(),
  tax_amount: z.number(),
  linked_order_id: ZodId.nullable(),
  nav_date: ZodDateTimeString.nullable(),
  nav: z.number().nullable(),
  equipment: ZodTrimmedString.nullable(),
  reference_period: ZodTrimmedString,
  tax_residence_code: ZodTaxResidenceCode.nullable(),
  instrument_name: ZodTrimmedString.nullable(),
  user_currency_amount_input: z.number().nullable(),
  filled_quantity: z.number().nullable(),
  operation_type: ZodB2bIndividualOperationType,
  plan_type: ZodB2bSavingPlanType,
  mc_gross_amount: z.number().nullable(),
  mc_net_amount: z.number().nullable(),
  mc_entry_fee_employee_amount: z.number().nullable(),
  mc_entry_fee_company_amount: z.number().nullable(),
  mc_tax_info: ZodJSONObject.nullable(),
  mc_filled_quantity: z.number().nullable(),
  mc_user_currency_amount_input: z.number().nullable(),
  mc_individual_operation_id: ZodId.nullable(),
  social_status: ZodTrimmedString,
});

export type GetB2bIndividualOperationOrderResponse = z.infer<
  typeof ZodGetB2bIndividualOperationOrderResponse
>;

const ZodGetB2bIndividualOperationOrdersResponse = z.array(
  ZodGetB2bIndividualOperationOrderResponse,
);
export type GetB2bIndividualOperationOrdersResponse = z.infer<
  typeof ZodGetB2bIndividualOperationOrdersResponse
>;

export const getB2bIndividualOperationOrdersEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetB2bIndividualOperationOrdersQuery,
  responseBody: ZodGetB2bIndividualOperationOrdersResponse,
});

export type GetB2bIndividualOperationOrdersEndpoint = GetEndpointType<
  typeof getB2bIndividualOperationOrdersEndpoint
>;

/** The orders of an individual operation, in the branded Excel template */
export const ZodIndividualOperationOrdersExportBody = z.object({
  kind: z.literal('individual_operation_orders'),
  individual_operation_id: ZodId,
});
export type IndividualOperationOrdersExportBody = z.infer<
  typeof ZodIndividualOperationOrdersExportBody
>;

export const postB2bIndividualOperationOrdersExportEndpoint = endpoint({
  method: 'POST',
  body: ZodIndividualOperationOrdersExportBody,
  responseBody: ZodBoExportResponse,
});
export type PostB2bIndividualOperationOrdersExportEndpoint = GetEndpointType<
  typeof postB2bIndividualOperationOrdersExportEndpoint
>;

export const BoVoluntaryDepositTypeValues = ['libre', 'fully_managed'] as const;
export const ZodBoVoluntaryDepositType = z.enum(BoVoluntaryDepositTypeValues);
export type BoVoluntaryDepositType = z.infer<typeof ZodBoVoluntaryDepositType>;

export const ZodBoVoluntaryDepositPaymentMethod = ZodB2BPaymentMethod.extract([
  'regular-bank',
  'sepa-direct-debit',
]);
export type BoVoluntaryDepositPaymentMethod = z.infer<typeof ZodBoVoluntaryDepositPaymentMethod>;

const ZodBoVoluntaryDepositCommon = z.object({
  b2b_employee_id: ZodId,
  saving_plan_id: ZodId,
  payment_method: ZodBoVoluntaryDepositPaymentMethod,
  deductibility: ZodVoluntaryDeductibility.optional(),
});

const ZodBoVoluntaryDepositLibreBody = z.object({
  ...ZodBoVoluntaryDepositCommon.shape,
  type: z.literal('libre'),
  allocations: ZodMutualFundOrderAllocations,
});

const ZodBoVoluntaryDepositFullyManagedBody = z.object({
  ...ZodBoVoluntaryDepositCommon.shape,
  type: z.literal('fully_managed'),
  amount: zodIntegerAmount2.and(z.number().min(10_00).max(FULLY_MANAGED_ORDER_LIMIT)),
});

const ZodPostB2bIndividualOperationVoluntaryDepositBody = z.discriminatedUnion('type', [
  ZodBoVoluntaryDepositLibreBody,
  ZodBoVoluntaryDepositFullyManagedBody,
]);

export type PostB2bIndividualOperationVoluntaryDepositBody = z.infer<
  typeof ZodPostB2bIndividualOperationVoluntaryDepositBody
>;

const ZodPostB2bIndividualOperationVoluntaryDepositBulkBody = z.object({
  type: z.literal('voluntary_deposit'),
  investments: z.array(ZodPostB2bIndividualOperationVoluntaryDepositBody).min(1),
});

export type PostB2bIndividualOperationVoluntaryDepositBulkBody = z.infer<
  typeof ZodPostB2bIndividualOperationVoluntaryDepositBulkBody
>;

const ZodPostB2bIndividualOperationVoluntaryDepositResponse = z.object({
  id: ZodId,
});

// An arbitrage is not created here: it goes through the b2b_arbitrage review request
export const postB2bIndividualOperationEndpoint = endpoint({
  method: 'POST',
  body: ZodPostB2bIndividualOperationVoluntaryDepositBulkBody,
  responseBody: ZodPostB2bIndividualOperationVoluntaryDepositResponse,
});

export type PostB2bIndividualOperationEndpoint = GetEndpointType<
  typeof postB2bIndividualOperationEndpoint
>;

const ZodB2bIndividualOperationSummaryRow = z.object({
  saving_plan_id: ZodId,
  amount: zodIntegerAmount2.optional(),
  allocations: z
    .array(
      z.object({
        instrument_id: ZodId.nullable(),
        amount: zodIntegerAmount2.min(0),
      }),
    )
    .optional(),
});

const ZodPostB2bIndividualOperationSummaryBody = z.object({
  type: z.literal('voluntary_deposit'),
  b2b_employee_id: ZodId,
  rows: z.array(ZodB2bIndividualOperationSummaryRow).min(1),
});

export type PostB2bIndividualOperationSummaryBody = z.infer<
  typeof ZodPostB2bIndividualOperationSummaryBody
>;

const ZodPostB2bIndividualOperationSummaryResponse = z.object({
  id: ZodId,
  total_matching_contribution_gross_amount: z.number().int(),
  // Entry fees are split between the payers: the portion deducted from the employee
  // and the portion absorbed by the company.
  total_entry_fee_employee_amount: z.number().int(),
  total_entry_fee_company_amount: z.number().int(),
});

export const postB2bIndividualOperationSummaryEndpoint = endpoint({
  method: 'POST',
  body: z.discriminatedUnion('type', [
    ZodPostB2bIndividualOperationSummaryBody,
    ZodPostB2bIndividualOperationArbitrageBody,
  ]),
  responseBody: z.union([
    ZodPostB2bIndividualOperationSummaryResponse,
    ZodPostB2bIndividualOperationArbitrageSummaryResponse,
  ]),
});

export type PostB2bIndividualOperationSummaryEndpoint = GetEndpointType<
  typeof postB2bIndividualOperationSummaryEndpoint
>;
